import { ChangeEvent, FocusEvent } from "react";
interface ScreenName {
    name: string
    step: 1 | 2 | 3 | 4
}

/**
 * List of all screens and the step they naturally occur on.
 * This can be in flux as we may get a screen from the client. and we may add a sign-up screen later.
 */
const screenNames: ScreenName[] = [
    {
        name: "contact_details",
        step: 1
    },
    {
        name: "attachment_upload",
        step: 2
    },
    {
        name: "review_application",
        step: 3
    },
    {
        name: "apply_confirmation",
        step: 4
    }
]

/**
 * Fnd and return the screen name and step number based on the input step number.
 */
const getScreenData = (step: number): ScreenName => screenNames.find((x) => x.step === step) ?? screenNames[0];

interface PayloadProps {
    values: Record<string, string>;
    link: string;
    resumeName: string;
    coverLetterName: string;
    snowplowId: string;
    companyId: string;
    atsJobId: string;
    service: string;
};

const getFileType = (file: string): string | null => {
    if (!file) return null;
    return file?.split(",")?.shift()?.replace("data:", "")?.replace(";base64", "") ?? null;
};

/**
 * Extract the content of a file from a string that represents the file, by splitting the string by commas and returning the last element of the resulting array.
 */
const getFileContent = (file: string): string | null => {
    if (!file) return null;
    return file?.split(",")?.pop() ?? null;
};

/**
 * Generate a JSON payload from the form data, including applicant contact information and any uploaded documents such
 * as a resume or cover letter.
 */
const getPayload = (props: PayloadProps): Record<string, any> => {
    const {
        values,
        link,
        resumeName,
        coverLetterName,
        snowplowId = "",
        companyId = "",
        atsJobId = "",
        service
    } = props;
    const {
        email,
        phone,
        lastName,
        firstName,
        resume,
        coverLetter
    } = values;

    const payload = {
        ats_job_apply_link: link,
        email,
        phone_number: `+${phone.replace(/\D/g, "")}`,
        last_name: lastName,
        first_name: firstName,
        resume: getFileContent(resume),
        resume_name: resumeName,
        resume_content_type: getFileType(resume),
        cover_letter: getFileContent(coverLetter),
        cover_letter_content_file_type: getFileType(coverLetter),
        cover_letter_name: coverLetterName,
        snowplow_id: snowplowId,
        company_id: companyId,
        job_id: atsJobId,
        service
    };

    return payload;
};

interface SubmitProps {
    link: string;
    endpoint: string;
    values: Record<string, string>;
    resumeName: string;
    coverLetterName: string;
    snowplowId: string;
    companyId: string;
    atsJobId: string;
    jobId?: number;
    service: string;
}

/**
 * Store the applicants details in session storage.
 */
const storeApplicant = (props: Record<string, string>) => {
    const {
        email,
        firstName,
        lastName,
        phone,
        resume,
        resumeStats,
        resumeName
    } = props;

    setStorageApplicant({
        email,
        firstName,
        lastName,
        phone,
        resume,
        resumeName,
        resumeStats,
    });
}

const storeJobApply = (id: number = 0) => {
    const jobInteractionHistory = localStorage.getItem("jobInteractionHistory") ?? "[]";
    const list = JSON.parse(jobInteractionHistory);
    const payload = [
        ...list.filter((x: any) => x.id !== id),
        { id, dt: new Date().getTime(), app: true }
    ];
    localStorage.setItem("jobInteractionHistory", JSON.stringify(payload));
    window.dispatchEvent(new Event("storage"));
}

/**
 * Submit the application data to the back end.
 * This is a JSON POST request, and all files will be 64bit encoded strings.
 */
const submitJobApply = async (props: SubmitProps) => {
    const {
        link,
        endpoint,
        values,
        resumeName,
        coverLetterName,
        snowplowId = "",
        companyId = "",
        atsJobId = "",
        jobId,
        service
    } = props;

    storeApplicant({
        ...values,
        resumeName
    });
    storeJobApply(jobId);

    const config = {
        method: "POST",
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(getPayload({
            values,
            link,
            resumeName,
            coverLetterName,
            snowplowId,
            companyId,
            atsJobId,
            service
        }))
    }

    const details = JSON.stringify({ snowplowId, companyId, atsJobId });
    const happy = "successfully sent";
    const sad = "encountered an error sending";
    const message = (msg: string) => `"The Direct Apply module ${msg} the request to ${endpoint}. Data: ${details}`;
    return await fetch(endpoint, config)
        .then((response: Response) => {
            if (response.status < 200 || response.status >= 300) {
                throw new Error("Status out of bounds.");
            }
            console.info(message(happy));
            return response;
        })
        .catch(() => {
            throw new Error(message(sad));
        });
};

/**
 * Retrieves the snowplow cookie and extracts the ID from it.
 * The function attempts to retrieve the cookie every 100ms and fails after 3 seconds if it is unable to retrieve it.
 */
const getSnowplowId = (setSnowplowId: Function) => {
    let requestId: number;
    const maxTimeout = setTimeout(() => {
        cancelAnimationFrame(requestId);
        clearTimeout(maxTimeout);
    }, 3000);

    /**
     * Attempts to retrieve the snowplow cookie and extract the ID from it.
     * If the ID is retrieved successfully, cancels the animation frame and clears the timeout,
     * sets 'hasChecked' to true, and sets the snowplow ID using 'setSnowplowId'.
     * If the ID is not retrieved, requests another animation frame.
     */
    const getCookieIds = () => {
        // Gather the cookies
        const { newCookie, oldCookie } = document.cookie.split(";").reduce((acc, current) => {
            const [key, val] = current.split("=");
            if (key.includes("_sp_id")) {
                acc.oldCookie = val;
            }
            if (key.includes("tmspid")) {
                acc.newCookie = val;
            }
            return acc;
        }, { newCookie: "", oldCookie: "" });

        // split off the Snowplow ID portion
        const snowPlowId = [newCookie, oldCookie].filter(Boolean)[0]?.split(".")[0] ?? "";

        // If we have a valid cookie use it and kill the loop.
        if (snowPlowId) {
            cancelAnimationFrame(requestId);
            clearTimeout(maxTimeout);
            setSnowplowId(snowPlowId);
            return;
        }
        // If we do not have a cookie LOOP (max 3 seconds)
        requestId = requestAnimationFrame(getCookieIds);
    };

    getCookieIds();
};
interface FieldProps {
    handleChange: {
        (e: ChangeEvent<any>): void;
        <T = string | ChangeEvent<any>> (field: T): T extends ChangeEvent<any> ? void : (e: string | ChangeEvent<any>) => void;
    };
    touchedObj: Record<string, string>;
    setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void;
}

const handleOnBlur = (props: FieldProps) => (e: FocusEvent<HTMLInputElement>) => {
    const { handleChange, setFieldTouched, touchedObj } = props;
    const fieldName = (e.target as HTMLInputElement).name;
    if (!touchedObj[fieldName]) {
        setFieldTouched(fieldName);
    }
    handleChange(e);
};

const handleOnChange = (props: FieldProps) => (e: FocusEvent<HTMLInputElement>) => {
    const { handleChange, touchedObj } = props;
    const fieldName = (e.target as HTMLInputElement).name;
    if (touchedObj[fieldName]) {
        handleChange(e);
    }
};

interface Applicant {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    resume: string;
    resumeName: string;
    resumeStats: string;
}

/**
 * Encrypts and stores an applicants data in the browser's local storage, along with a last modified date.
 */
const setStorageApplicant = (user: Applicant) => {
    const lastModified = Date.now().toString();
    const stringifiedUser = JSON.stringify(user);
    const encryptedUser = window.btoa(stringifiedUser);

    localStorage.setItem("ApplicantModified", lastModified);
    window.dispatchEvent(new Event("storage"));
    localStorage.setItem("Applicant", encryptedUser);
    window.dispatchEvent(new Event("storage"));
};

/**
 * Retrieves an encrypted applicant object from the browser's local storage, along with a last modified date.
 * If expired deletes the applicant and returns an empty object
 * If active decrypts the applicant and returns it.
 */
const getStorageApplicant = (): Applicant => {
    const lastModified = localStorage.getItem("ApplicantModified") ?? "0";
    const lastModifiedNumber = parseInt(lastModified, 10);
    const lastModifiedDate: Date = new Date(lastModifiedNumber);
    const expires: number = new Date(lastModifiedDate).valueOf() + 30 * 24 * 60 * 60 * 1000; // 30 days

    if (expires >= new Date().valueOf()) {
        const applicant = localStorage.getItem("Applicant") ?? "";
        try {
            const decrypted = window.atob(applicant);
            const parsed = JSON.parse(decrypted.length ? decrypted : "{}");
            return parsed;
        } catch (err) {
            console.error(err);
            localStorage.removeItem("DirectApply");
            return {} as Applicant;
        }
    }

    localStorage.removeItem("DirectApply");
    return {} as Applicant;
};

export {
    getScreenData,
    getPayload,
    getSnowplowId,
    submitJobApply,
    handleOnChange,
    handleOnBlur,
    setStorageApplicant,
    getStorageApplicant
};
export type { Applicant };
